import { ChartLegendWrapper, LineTimeSerieChart } from '@scality/core-ui/dist/next';
import type { JSONSchema7 } from 'json-schema';
import type React from 'react';
import { useMemo } from 'react';
import { useLineChartParams } from '../../../hooks/useLineChartParams';
import { legendInstancesOf } from '../../../portal/instanceMarker';
import { ChartBody, ChartCard } from '../../chat-messages/ChartWrapper';
import type { AIToolComponent } from '../../chat-messages/GuardianMarkdown';
import { ResolveInstanceMarkers } from '../../ResolveInstanceMarkers';
import { NoData } from '../NoData';
import { ChartLegendPanel } from '../shared/ChartLegendPanel';
import { buildTimeSeriesTable } from '../shared/chartExportTables';
import { type LineChartBase, type LineChartData, type PointTimeSerie, toPointTimeSeries } from './timeSeries';

const UNIT_RANGE_DISABLE = [{ label: '', threshold: 1 }]; // as of core-ui 167 undefined unitRange crashes

const WINDOW_FORMAT = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });

/** The window the enlarged plot covers, so a reader knows what they are looking at. */
const formatWindow = (startTime?: number | null, endTime?: number | null): string | undefined =>
  startTime != null && endTime != null
    ? `${WINDOW_FORMAT.format(new Date(startTime))} → ${WINDOW_FORMAT.format(new Date(endTime))}`
    : undefined;

type LineChartViewProps = LineChartBase & {
  series: PointTimeSerie[];
  interval?: number | null;
  instances: ReturnType<typeof legendInstancesOf>;
};

const LineChartView: React.FC<LineChartViewProps> = ({
  title,
  yAxisTitle,
  yAxisType,
  yUnitRange,
  yAxisScale,
  series,
  startTime,
  endTime,
  interval: sampleInterval,
  instances,
}) => {
  const unitRange = yUnitRange ?? UNIT_RANGE_DISABLE;

  const { coreUiSeries, colorSet, startingTimeStamp, interval, duration } = useLineChartParams({
    series,
    startTime,
    endTime,
    interval: sampleInterval,
  });

  // An empty widget says so rather than drawing an axis over nothing. Without a window
  // stamped on it there is not even an axis to draw: the params above come back as
  // Infinity / -Infinity, which core-ui cannot plot.
  if (!series.some((serie) => serie.data.length > 0)) {
    return (
      <ChartCard>
        <NoData title={title} />
      </ChartCard>
    );
  }

  return (
    <ChartCard
      title={title}
      meta={formatWindow(startTime, endTime)}
      buildExportTable={() => buildTimeSeriesTable({ name: title, series })}
    >
      {(view) => (
        <ChartLegendWrapper colorSet={colorSet}>
          <ChartBody legend={<ChartLegendPanel instances={instances} />}>
            <LineTimeSerieChart
              title={title}
              yAxisTitle={yAxisTitle}
              yAxisType={yAxisType ?? 'default'}
              yAxisScale={yAxisScale ?? 'linear'}
              series={coreUiSeries}
              height={view.chartHeight}
              startingTimeStamp={startingTimeStamp}
              interval={interval}
              duration={duration}
              unitRange={unitRange}
              rightTitle={view.actions}
            />
          </ChartBody>
        </ChartLegendWrapper>
      )}
    </ChartCard>
  );
};

// The chart itself reads names — a marker in a tooltip or as a colour key would be unreadable —
// and the legend badges the deployment those names came from.
export const LineChart: AIToolComponent<LineChartData> = ({ params }) => {
  const pointSeries = useMemo(() => toPointTimeSeries(params), [params]);
  const interval = params.timestamps === 'raw' ? undefined : params.interval;
  return (
    <ResolveInstanceMarkers
      value={pointSeries}
      render={(series) => (
        <LineChartView
          {...params}
          series={series}
          interval={interval}
          instances={legendInstancesOf(pointSeries, series)}
        />
      )}
    />
  );
};

LineChart.description = 'Displays a line chart to display continuous data.';

const SERIE_LABEL: Record<string, JSONSchema7> = {
  label: { type: 'string', description: 'The label of the data series.' },
  isLineDashed: {
    type: 'boolean',
    description: 'Draw the line dashed rather than solid, e.g. for a headroom line.',
    default: false,
  },
};

const STEPPED_SERIE: JSONSchema7 = {
  type: 'object',
  properties: {
    ...SERIE_LABEL,
    y: {
      type: 'array',
      description: 'Values in time order, y[i] at steppedStart + i × interval; null where nothing was reported.',
      items: { type: ['number', 'null'] },
    },
  },
  required: ['label', 'y'],
  additionalProperties: false,
};

const RAW_SERIE: JSONSchema7 = {
  type: 'object',
  properties: {
    ...SERIE_LABEL,
    x: { type: 'array', description: 'Unix timestamp in milliseconds of each value.', items: { type: 'number' } },
    y: { type: 'array', description: 'Values in time order, y[i] sampled at x[i].', items: { type: 'number' } },
  },
  required: ['label', 'x', 'y'],
  additionalProperties: false,
};

const POINT_SERIE: JSONSchema7 = {
  type: 'object',
  properties: {
    ...SERIE_LABEL,
    data: {
      type: 'array',
      minItems: 2,
      description: 'Points of a chart saved before series were sent as columns, as {x, y} pairs.',
      items: {
        type: 'object',
        properties: {
          x: { type: 'number', description: 'The x value.' },
          y: { type: 'number', description: 'The y value.' },
        },
        required: ['x', 'y'],
      },
    },
  },
  required: ['label', 'data'],
};

LineChart.schema = {
  type: 'object',
  properties: {
    title: {
      type: 'string',
      description: 'The title of the chart.',
    },
    yAxisTitle: {
      type: 'string',
      description: 'The title of the Y axis. Defaults to the chart title if not provided.',
    },
    yAxisType: {
      type: 'string',
      enum: ['default', 'percentage'],
      description: 'The type of the Y axis.',
      default: 'default',
    },
    yAxisScale: {
      type: 'string',
      enum: ['linear', 'log'],
      description:
        "Y-axis scale. 'log' gives each decade the same height, for values spanning orders of magnitude where a linear axis flattens the small ones onto the baseline. Display only: no value is rescaled.",
      default: 'linear',
    },
    yUnitRange: {
      type: 'array',
      description: 'Optional unit range for automatic unit scaling based on value thresholds.',
      items: {
        type: 'object',
        properties: {
          threshold: { type: 'number', description: 'Value threshold for this unit.' },
          label: { type: 'string', description: "Unit label to display (e.g., 'MiB/s')." },
        },
        required: ['threshold', 'label'],
      },
    },
    startTime: {
      type: ['integer', 'null'],
      description:
        'Start of the queried window, unix timestamp in milliseconds. Given it, every chart of a dashboard draws the same time axis instead of cropping to its own data.',
    },
    endTime: {
      type: ['integer', 'null'],
      description: 'End of the queried window, unix timestamp in milliseconds.',
    },
    timestamps: {
      type: 'string',
      enum: ['stepped', 'raw'],
      description:
        "How the series carry their timestamps: 'stepped' shares steppedStart + i × interval across every series, 'raw' gives each series its own x. Absent on a chart saved before columns.",
    },
    steppedStart: {
      type: 'integer',
      description: "Stepped chart: timestamp of every series' first value, unix milliseconds.",
    },
    interval: {
      type: ['integer', 'null'],
      description: 'Step in seconds between two values, also used to fill gaps with missing points.',
    },
    series: {
      type: 'array',
      minItems: 1,
      description: 'The data series to display in the chart, in the shape `timestamps` names.',
    },
  },
  required: ['title', 'series'],
  additionalProperties: false,
  oneOf: [
    {
      properties: {
        timestamps: { const: 'stepped' },
        interval: { type: 'integer', exclusiveMinimum: 0 },
        series: { items: STEPPED_SERIE },
      },
      required: ['timestamps', 'steppedStart', 'interval'],
    },
    {
      properties: { timestamps: { const: 'raw' }, series: { items: RAW_SERIE } },
      required: ['timestamps'],
    },
    { not: { required: ['timestamps'] }, properties: { series: { items: POINT_SERIE } } },
  ],
};

LineChart.displayInPanel = true;
